import { Button } from '@solid/shared/ui';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { filter } from 'rxjs/operators';
import { AuthContext } from '../../contexts/auth.context';
import { getCheckoutUrl, openPaddlePopup, paddleEvent } from '../../services/payment.service';
import * as StyledPaymentPopup from './PaymentPopup.styles';
import * as Styled from './PaymentPopupCheckout.styles';
import { Trans, useTranslation } from 'react-i18next';

interface PaymentPopupCheckoutProps {
  onComplete: () => void;
  onBack: () => void;
}

const PaymentPopupCheckout: React.FC<PaymentPopupCheckoutProps> = ({ onComplete, onBack }) => {
  const { auth } = useContext(AuthContext);
  const [paymentSuccess, setPaymentSuccess] = useState<'paddle' | 'invoice' | null>(null);
  const [invoiceComment, setInvoiceComment] = useState('');
  const { t } = useTranslation();
  const {
    mutate: startCheckout,
    isLoading,
    error,
  } = useMutation<void, Error, void>(async () => {
    const url = await getCheckoutUrl(invoiceComment);
    openPaddlePopup(url);
  });

  const mailToLink = useMemo(() => {
    const subject = encodeURIComponent(
      t('shared.paymentEmail.subject', {
        companyName: auth.user.companyBillingName || auth.user.companyName
      })
    );

    let emailBody = [
      t('shared.paymentEmail.bodyGreeting'),
      '',
      t('shared.paymentEmail.bodyBooking'),
      '',
      t('shared.paymentEmail.bodyDetails'),
      '',
      t('shared.paymentEmail.bodyCompanyName', { companyName: auth.user.companyName }),
      t('shared.paymentEmail.bodyStreet', { companyStreet: auth.user.companyStreet }),
      t('shared.paymentEmail.bodyCity', { companyCity: auth.user.companyCity }),
      t('shared.paymentEmail.bodyZipCode', { companyZipCode: auth.user.companyZipCode }),
      t('shared.paymentEmail.bodyCountry', { companyCountry: auth.user.companyCountry }),
      t('shared.paymentEmail.bodyVatId', { companyVatId: auth.user.companyVatId }),
      t('shared.paymentEmail.bodyBillingEmail', { billingEmail: auth.user.companyEmail || auth.user.email })
    ].join('\n');

    // Only include billing address if it exists and is different
    if (auth.user.companyDifferentBillingAddress) {
      emailBody += [
        '',
        '',
        t('shared.paymentEmail.bodyDifferentBillingAddress'),
        '',
        t('shared.paymentEmail.bodyBillingName', { companyBillingName: auth.user.companyBillingName }),
        t('shared.paymentEmail.bodyBillingStreet', { companyBillingStreet: auth.user.companyBillingStreet }),
        t('shared.paymentEmail.bodyBillingCity', { companyBillingCity: auth.user.companyBillingCity }),
        t('shared.paymentEmail.bodyBillingZipCode', { companyBillingZipCode: auth.user.companyBillingZipCode }),
        t('shared.paymentEmail.bodyBillingCountry', { companyBillingCountry: auth.user.companyBillingCountry })
      ].join('\n');
    }

    // Only include comment if it exists
    if (invoiceComment) {
      emailBody += '\n\n' + t('shared.paymentEmail.bodyInvoiceComment', { invoiceComment });
    }

    emailBody += [
      '',
      t('shared.paymentEmail.bodyUserId', { userId: auth.user.id }),
      '',
      '',
      t('shared.paymentEmail.bodyContract'),
      t('shared.paymentEmail.bodyCancellation'),
      '',
      t('shared.paymentEmail.bodySignature'),
      '',
      `${auth.user.firstName} ${auth.user.lastName}`
    ].join('\n');

    return `mailto:info@solid-app.de?subject=${subject}&body=${encodeURIComponent(emailBody)}`;
  }, [auth, invoiceComment, t]);

  useEffect(() => {
    // Show thank you message upon successfully completing the checkout
    paddleEvent
      .pipe(
        filter(data => {
          return (
            data.event === 'Checkout.Close' &&
            (data.eventData as { checkout: { completed: boolean } }).checkout.completed === true
          );
        })
      )
      .subscribe(() => setPaymentSuccess('paddle'));
  }, []);

  return (
    <Styled.Wrapper>
      {!paymentSuccess ? (
        <Styled.Headline>
          <Trans i18nKey='shared.paymentPopupCheckout.selectPaymentMethodText' components={{ span: <span /> }} />
        </Styled.Headline>
      ) : (
        <Styled.Headline>
          <Trans i18nKey='shared.paymentPopupCheckout.paymentSuccessText' components={{ span: <span /> }} />
        </Styled.Headline>
      )}

      {!paymentSuccess ? (
        <div>
          <Styled.InvoiceCommentContainer className='form-row'>
            <div className='form-field'>
              <label htmlFor='invoiceComment'>{t('shared.paymentPopupCheckout.invoiceComment')}</label>
              <div className='control'>
                <input
                  type='text'
                  name='invoiceComment'
                  id='invoiceComment'
                  placeholder={t('shared.paymentPopupCheckout.orderNumber')}
                  maxLength={500}
                  value={invoiceComment}
                  onChange={e => setInvoiceComment(e.currentTarget.value)}
                />
              </div>
            </div>
          </Styled.InvoiceCommentContainer>

          <Styled.PaymentMethodContainer>
            <Button onClick={() => setPaymentSuccess('invoice')} href={mailToLink}>
              {t('shared.paymentPopupCheckout.paymentMethodInvoice')}
            </Button>
            <Button isLoading={isLoading} onClick={() => startCheckout()}>
              {t('shared.paymentPopupCheckout.paymentMethodCreditCard')}
            </Button>
          </Styled.PaymentMethodContainer>
        </div>
      ) : paymentSuccess === 'invoice' ? (
        // Invoice success
        <Styled.SuccessMessage>
          <p>
            <Trans i18nKey='shared.paymentPopupCheckout.paymentSuccessInvoiceText' components={{ br: <br /> }} />
          </p>

          <Button variant='outlined' href={mailToLink}>
            {t('shared.paymentPopupCheckout.emailButtonText')}
          </Button>

          <p>
            <Trans
              i18nKey='shared.paymentPopupCheckout.questionsText'
              components={{ a: <a href='mailto:info@solid-app.de' /> }}
            />
          </p>

          <Link to='/' onClick={() => onComplete()}>
            {t('shared.paymentPopupCheckout.toChecksButtonText')}
          </Link>
        </Styled.SuccessMessage>
      ) : (
        // Paddle success
        <Styled.SuccessMessage>
          <p>{t('shared.paymentPopupCheckout.paymentSuccessPaddleText')}</p>

          <p>
            <Trans
              i18nKey='shared.paymentPopupCheckout.questionsText'
              components={{ a: <a href='mailto:info@solid-app.de' /> }}
            />
          </p>

          <Link to='/' onClick={() => onComplete()}>
            {t('shared.paymentPopupCheckout.toChecksButtonText')}
          </Link>
        </Styled.SuccessMessage>
      )}

      {error && <div className='notification is-error'>{error.message}</div>}

      {!paymentSuccess && (
        <StyledPaymentPopup.BtnContainer $justifyContent='left'>
          <Button variant='outlined' onClick={() => onBack()}>
            {t('common.back')}
          </Button>
        </StyledPaymentPopup.BtnContainer>
      )}
    </Styled.Wrapper>
  );
};

export default PaymentPopupCheckout;
